import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { ClientLoggingService } from '../../../../../shared/client-logging.service';
import {
  PageScaleCalibrationService,
  PageScaleError,
  PageScalePageRef,
  PageScaleWriteResult,
} from '../../../../../shared/page-scale-calibration.service';
import { PageRotation, clamp01 } from '../../../../../shared/page-region/region-geometry';
import {
  AUTOMATIC_PAGE_SCALE,
  AutomaticScale,
  EffectiveScale,
  NormalizedLine,
  PageScaleHistoryEntry,
  PageScaleState,
  PageSizePoints,
  ScaleWarning,
  describeScaleFactor,
  displayLineToPage,
  displayedPageSize,
  pageLineToDisplay,
  resolveEffectiveScale,
} from '../measure-overlay/scale-calibration';
import { ApplyCalibrationEvent } from './scale-calibration-panel.component';

/** The part of the page that is visible on screen, as fractions of the displayed page. */
export interface VisiblePageArea {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** What the Preview should do after a controller action that talks to the server. */
export interface ScaleActionResult {
  /** True when the change was saved. */
  applied: boolean;
  /** A short message to show the reviewer, e.g. in a snackbar; empty when there is none. */
  notice: string;
}

const NO_AUTOMATIC_SCALE: AutomaticScale = { scaleText: null, scaleFactor: null };

/**
 * Holds the scale state of the page shown in one Preview and carries out the calibration workflow
 * (Issue #2442).
 *
 * The Preview already coordinates rendering, zoom, gestures and several overlays. This class keeps
 * the calibration rules out of it: which scale is in force, what the reviewer has traced so far,
 * and what happens when a save meets a conflict, a warning, or a changed page. The Preview tells it
 * which page is on screen and how that page is turned, forwards the toolbar and panel events, and
 * binds its fields to the measure overlay, the toolbar and the calibration panel.
 *
 * One instance belongs to one Preview. It is provided by the Preview component, so its state is
 * discarded with the Preview.
 */
@Injectable()
export class ScaleCalibrationController {
  private api = inject(PageScaleCalibrationService);

  /** The page's saved scale state, as last reported by the server. */
  state: PageScaleState = { ...AUTOMATIC_PAGE_SCALE };
  /** The scale detected in the page transcript. */
  automatic: AutomaticScale = NO_AUTOMATIC_SCALE;
  /** True while the detected scale is temporarily used instead of the manual one. */
  comparing = false;
  /** Unrotated page size in PDF points; null until the page is rendered. */
  pageSize: PageSizePoints | null = null;
  /** Page size in PDF points as displayed, i.e. after rotation. */
  displayedSize: PageSizePoints | null = null;
  rotation: PageRotation = 0;
  correctionFactor = 1;
  /** The scale the measurement tools use right now. */
  effective: EffectiveScale = this.resolve();

  /** Which view of the calibration panel is open, if any. */
  panelView: 'calibrate' | 'details' | null = null;
  /** The line traced so far, in unrotated page space; null until both ends are placed. */
  draftLine: NormalizedLine | null = null;
  /** A line for the overlay to show, in display space. */
  presetLine: NormalizedLine | null = null;
  /** True while a save or reset is in flight. */
  busy = false;
  serverMessage: string | null = null;
  serverWarnings: ScaleWarning[] = [];
  /** The page's calibration history; null until loaded. */
  history: PageScaleHistoryEntry[] | null = null;
  /** Which side of the Preview the calibration panel is docked on. */
  panelPlacement: 'left' | 'right' = 'left';

  private readonly changedSubject = new Subject<void>();
  /** Emits after any state changes outside a direct call, so the Preview can refresh its view. */
  readonly changed$ = this.changedSubject.asObservable();

  private page: PageScalePageRef | null = null;
  /** Incremented for every page shown, so an answer for an earlier page is ignored. */
  private pageGeneration = 0;

  // ── Page and geometry ─────────────────────────────────────────

  /**
   * Tells the controller which page is on screen.
   *
   * The detected scale is applied immediately. The saved calibration is then fetched, and applied
   * only if the same page is still on screen when the answer arrives. A failure to fetch leaves the
   * page on its detected scale: a missing calibration must never block measuring.
   */
  async showPage(page: PageScalePageRef | null, automatic: AutomaticScale, correctionFactor: number): Promise<void> {
    const generation = ++this.pageGeneration;
    const samePage =
      !!page &&
      !!this.page &&
      page.projectId === this.page.projectId &&
      page.fileId === this.page.fileId &&
      page.pageNumber === this.page.pageNumber;
    this.page = page;
    this.automatic = automatic;
    this.correctionFactor = correctionFactor > 0 ? correctionFactor : 1;
    if (!samePage) {
      this.state = { ...AUTOMATIC_PAGE_SCALE };
      this.comparing = false;
      this.clearDraft();
      this.panelView = null;
      this.history = null;
    }
    this.refresh();
    if (!page) return;
    try {
      const state = await this.api.get(page);
      if (generation !== this.pageGeneration) return;
      this.state = state;
      if (state.status !== 'manual') this.comparing = false;
    } catch (error) {
      if (generation !== this.pageGeneration) return;
      ClientLoggingService.log('WARN', '[ScaleCalibration] Saved page scale could not be loaded', error);
    }
    this.refresh();
    this.changedSubject.next();
  }

  /** Tells the controller the page's size and rotation, after every render. */
  setGeometry(pageSize: PageSizePoints | null, rotation: PageRotation): void {
    const sizeChanged = pageSize?.width !== this.pageSize?.width || pageSize?.height !== this.pageSize?.height;
    if (!sizeChanged && rotation === this.rotation) return;
    this.pageSize = pageSize ? { ...pageSize } : null;
    this.displayedSize = pageSize ? displayedPageSize(pageSize, rotation) : null;
    if (rotation !== this.rotation) {
      this.rotation = rotation;
      // The traced line is kept in page space, so it turns with the page: hand the overlay the
      // line's new position on the turned page.
      if (this.draftLine) {
        this.presetLine = pageLineToDisplay(this.draftLine, rotation);
      }
    }
    this.refresh();
  }

  // ── Calibration workflow ──────────────────────────────────────

  /**
   * Opens the calibration panel with nothing traced yet.
   *
   * @param fromSaved start from the saved reference line, for recalibrating
   */
  beginCalibration(fromSaved = false): void {
    this.clearDraft();
    this.panelView = 'calibrate';
    const saved = fromSaved ? this.state.calibration : null;
    if (saved) {
      this.draftLine = saved.reference;
      this.presetLine = pageLineToDisplay(saved.reference, this.rotation);
    }
  }

  /**
   * Opens the calibration panel with a line the reviewer already drew with the ruler, so "Use as
   * scale reference" continues from the measurement instead of asking for the line again
   * (Issue #2542).
   *
   * @param displayLine the ruler line as fractions of the displayed page
   */
  beginCalibrationFromLine(displayLine: NormalizedLine): void {
    this.beginCalibration();
    this.presetLine = displayLine;
    this.onLineTraced(displayLine);
  }

  /** Moves the calibration panel to the other side of the Preview, at the reviewer's request. */
  movePanel(): void {
    this.panelPlacement = this.panelPlacement === 'left' ? 'right' : 'left';
  }

  /** Leaves calibration without saving anything. */
  cancelCalibration(): void {
    this.clearDraft();
    if (this.panelView === 'calibrate') this.panelView = null;
  }

  /** Records the line the reviewer traced or adjusted on the displayed page. */
  onLineTraced(displayLine: NormalizedLine | null): void {
    this.draftLine = displayLine ? displayLineToPage(displayLine, this.rotation) : null;
    // A changed line is a different calibration; earlier answers about it no longer apply.
    this.serverMessage = null;
    this.serverWarnings = [];
  }

  /**
   * Places a horizontal line across the middle of the visible part of the page, for a reviewer who
   * cannot or does not want to point at the page. Its ends can then be moved with the arrow keys.
   */
  placeLine(visible: VisiblePageArea): void {
    const left = clamp01(visible.left);
    const right = clamp01(visible.right);
    const y = clamp01((clamp01(visible.top) + clamp01(visible.bottom)) / 2);
    const width = Math.max(0, right - left);
    const line: NormalizedLine = {
      start: { x: left + width * 0.3, y },
      end: { x: left + width * 0.7, y },
    };
    this.presetLine = line;
    this.onLineTraced(line);
  }

  /**
   * Stops showing the reference line as a ruler measurement. Called when the reviewer moves on to
   * another tool after a calibration was applied, so the line does not reappear later.
   */
  forgetPresetLine(): void {
    if (this.panelView !== 'calibrate') this.presetLine = null;
  }

  /** Discards the traced line so a new one can be drawn. */
  redrawLine(): void {
    this.presetLine = null;
    this.onLineTraced(null);
  }

  /** Saves the traced line and typed length as the page's calibration. */
  async apply(event: ApplyCalibrationEvent): Promise<ScaleActionResult> {
    const page = this.page;
    const line = this.draftLine;
    if (!page || !line || this.busy) return { applied: false, notice: '' };
    this.busy = true;
    this.serverMessage = null;
    try {
      const result = await this.api.save({
        ...page,
        reference: line,
        enteredValue: event.enteredValue,
        note: event.note,
        automatic: this.automatic,
        expectedEtag: this.state.etag,
        expectedPageRevision: this.state.currentPageRevision || undefined,
        pageSizeCorrectionFactor: this.correctionFactor,
        acknowledgeWarnings: event.acknowledgeWarnings,
      });
      if (!this.isCurrent(page)) return { applied: false, notice: '' };
      return this.afterWrite(result, () => {
        // Keep the reference on screen as an ordinary measurement, so the reviewer sees it read
        // the length they entered.
        this.presetLine = pageLineToDisplay(line, this.rotation);
        this.draftLine = null;
        this.panelView = null;
        const factor = result.state.calibration?.drawingScaleFactor;
        return `Scale calibrated${factor ? ` to ${describeScaleFactor(factor)}` : ''}. Measurements on this page now use it.`;
      });
    } catch (error) {
      this.serverMessage = error instanceof PageScaleError ? error.message : 'The calibration could not be saved.';
      return { applied: false, notice: '' };
    } finally {
      this.busy = false;
      this.changedSubject.next();
    }
  }

  /** Removes the page's manual calibration so that automatic detection applies again. */
  async reset(): Promise<ScaleActionResult> {
    const page = this.page;
    if (!page || this.busy) return { applied: false, notice: '' };
    this.busy = true;
    this.serverMessage = null;
    try {
      const result = await this.api.reset(page, this.state.etag);
      if (!this.isCurrent(page)) return { applied: false, notice: '' };
      const outcome = this.afterWrite(result, () => {
        this.panelView = null;
        return this.automatic.scaleFactor !== null
          ? 'Calibration removed. This page uses its detected scale again.'
          : 'Calibration removed. No scale is detected on this page.';
      });
      // A reset has no panel of its own to explain a refusal in, so the explanation is the notice.
      return outcome.applied ? outcome : { applied: false, notice: this.serverMessage ?? '' };
    } catch (error) {
      this.serverMessage = error instanceof PageScaleError ? error.message : 'The calibration could not be reset.';
      return { applied: false, notice: this.serverMessage };
    } finally {
      this.busy = false;
      this.changedSubject.next();
    }
  }

  /** Switches between the manual scale and the detected scale, without saving anything. */
  toggleCompare(): void {
    if (this.state.status !== 'manual') return;
    this.comparing = !this.comparing;
    this.refresh();
  }

  /** Opens the details of the saved calibration and loads its history. */
  async showDetails(): Promise<void> {
    const page = this.page;
    if (!page) return;
    this.panelView = 'details';
    this.serverMessage = null;
    this.history = null;
    try {
      const history = await this.api.history(page);
      if (!this.isCurrent(page)) return;
      this.history = history;
    } catch (error) {
      if (!this.isCurrent(page)) return;
      this.history = [];
      this.serverMessage = error instanceof PageScaleError ? error.message : 'The history could not be loaded.';
    }
    this.changedSubject.next();
  }

  closeDetails(): void {
    if (this.panelView === 'details') this.panelView = null;
  }

  // ── What the toolbar shows ────────────────────────────────────

  /** The toolbar state of Calibrate scale. */
  get toolbarStatus(): 'manual' | 'automatic' | 'none' | 'invalidated' {
    if (this.state.status === 'manual') return 'manual';
    if (this.state.status === 'invalidated') return 'invalidated';
    return this.automatic.scaleFactor !== null ? 'automatic' : 'none';
  }

  /** The scale in force, in words, for the toolbar's tooltip and accessible name. */
  get summary(): string {
    if (this.state.status === 'invalidated') {
      return `Manual calibration not applied because the page changed. ${this.effective.label}`;
    }
    return this.effective.label;
  }

  // ── Internals ─────────────────────────────────────────────────

  /**
   * Applies a write answer. On `applied` the state is replaced and `onApplied` supplies the notice.
   * Otherwise the state still moves to what the server reported, so a retry is made against the
   * current version, and the explanation is shown in the panel.
   */
  private afterWrite(result: PageScaleWriteResult, onApplied: () => string): ScaleActionResult {
    this.state = result.state;
    this.serverWarnings = result.outcome === 'needs-confirmation' ? result.warnings : [];
    if (result.outcome !== 'applied') {
      this.serverMessage = result.message || 'Nothing was saved. Review the current scale and try again.';
      if (this.state.status !== 'manual') this.comparing = false;
      this.refresh();
      return { applied: false, notice: '' };
    }
    this.comparing = false;
    this.history = null;
    const notice = onApplied();
    this.refresh();
    return { applied: true, notice };
  }

  /**
   * Whether an answer for `page` still belongs on screen. Preview hands over a new reference each
   * time it shows a page, so the same page is recognized by its project, file and number.
   */
  private isCurrent(page: PageScalePageRef): boolean {
    return (
      !!this.page &&
      this.page.projectId === page.projectId &&
      this.page.fileId === page.fileId &&
      this.page.pageNumber === page.pageNumber
    );
  }

  private clearDraft(): void {
    this.draftLine = null;
    this.presetLine = null;
    this.serverMessage = null;
    this.serverWarnings = [];
  }

  private refresh(): void {
    this.effective = this.resolve();
  }

  private resolve(): EffectiveScale {
    return resolveEffectiveScale({
      state: this.state,
      automatic: this.automatic,
      compareWithAutomatic: this.comparing,
      pageSize: this.pageSize,
      correctionFactor: this.correctionFactor,
    });
  }
}
